import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, TextField, IconButton, Tooltip } from '@mui/material';

import { Icon } from '@liftango/ui-icons';
const COLORS = ['#1976d2', '#ff9800', '#43a047'];

interface RidePattern {
  p2p: number;
  p2h: number;
  h2h: number;
}

interface RidePatternEditorProps {
  ridePattern: RidePattern;
  setRidePattern: (pattern: RidePattern) => void;
  disabled?: boolean;
  numHubs?: number;
  onlyH2HDisabled?: boolean;
}

const clamp = (val: number) => Math.max(0, Math.min(100, val));

const RidePatternEditor: React.FC<RidePatternEditorProps> = ({ ridePattern, setRidePattern, disabled, numHubs, onlyH2HDisabled }) => {
  const { t } = useTranslation('mapWizards');
  const { p2p, p2h, h2h } = ridePattern;
  const h2hDisabled = disabled || onlyH2HDisabled || (numHubs !== undefined && numHubs < 2);
  // Always show the actual value for H2H, even if disabled
  const effectiveH2H = h2h;
  const preventWheelValueChange = (event: React.WheelEvent<HTMLInputElement>) => {
    event.currentTarget.blur();
  };

  // Lock state for each field
  const [locked, setLocked] = useState<{ p2p: boolean; p2h: boolean; h2h: boolean }>({ p2p: false, p2h: false, h2h: false });

  // Only one field can be locked at a time. Allow switching lock in a single click.
  const canLock = (field: keyof RidePattern) => {
    // Always allow unlocking
    if (locked[field]) return true;
    // Allow locking if no field is locked or if another is locked (to switch lock)
    const numLocked = Object.values(locked).filter(Boolean).length;
    return numLocked <= 1;
  };

  const handleLockToggle = (field: keyof RidePattern) => {
    if (locked[field]) {
      // Unlock this field
      setLocked(prev => ({ ...prev, [field]: false }));
    } else {
      // If another field is locked, unlock it and lock this one
      const lockedField = (Object.keys(locked) as (keyof RidePattern)[]).find(f => locked[f]);
      if (lockedField && lockedField !== field) {
        setLocked(prev => ({ ...prev, [lockedField]: false, [field]: true }));
      } else {
        setLocked(prev => ({ ...prev, [field]: true }));
      }
    }
  };

  const handleChange = (field: keyof RidePattern) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (locked[field]) return;
    let value = clamp(Number(e.target.value));
    let newPattern: RidePattern = { ...ridePattern };
    newPattern[field] = value;
    // Get unlocked fields (excluding the one being changed)
    const otherFields = (['p2p', 'p2h', 'h2h'] as (keyof RidePattern)[]).filter(f => f !== field && !locked[f]);
    const lockedFields = (['p2p', 'p2h', 'h2h'] as (keyof RidePattern)[]).filter(f => f !== field && locked[f]);
    // Sum of locked values
    let lockedSum = lockedFields.reduce((sum, f) => sum + (f === 'h2h' && h2hDisabled ? 0 : ridePattern[f]), 0);
    // If H2H is disabled, force it to 0
    if (h2hDisabled) {
      newPattern.h2h = 0;
      lockedSum = lockedFields.reduce((sum, f) => sum + (f !== 'h2h' ? ridePattern[f] : 0), 0);
    }
    let remainder = 100 - value - lockedSum;
    // Distribute remainder among unlocked fields
    if (otherFields.length === 1) {
      newPattern[otherFields[0]] = clamp(remainder);
    } else if (otherFields.length === 2) {
      // Distribute proportionally to current values
      const total = otherFields.reduce((sum, f) => sum + (f === 'h2h' && h2hDisabled ? 0 : ridePattern[f]), 0) || 1;
      newPattern[otherFields[0]] = clamp(Math.round((ridePattern[otherFields[0]] / total) * remainder));
      newPattern[otherFields[1]] = clamp(remainder - newPattern[otherFields[0]]);
    }
    setRidePattern(newPattern);
  };

  const segments = [
    { label: 'P2P', value: p2p, color: COLORS[0] },
    { label: 'P2H', value: p2h, color: COLORS[1] },
    { label: 'H2H', value: effectiveH2H, color: COLORS[2] },
  ];

  return (
    <Box sx={{ mt: 3, mb: 2 }}>
      <Typography variant="subtitle1">{t('generateRides.ridePattern.title')}</Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t('generateRides.ridePattern.descriptionFull')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {(['p2p', 'p2h', 'h2h'] as (keyof RidePattern)[]).map((field, idx) => (
          <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label={field.toUpperCase()}
              type="number"
              value={ridePattern[field]}
              onChange={handleChange(field)}
              onWheel={preventWheelValueChange}
              size="small"
              sx={{ width: 90 }}
              inputProps={{ min: 0, max: 100 }}
              disabled={
                disabled ||
                (field === 'h2h' && h2hDisabled) ||
                locked[field] ||
                (field === 'h2h' && onlyH2HDisabled)
              }
            />
            <Tooltip title={locked[field] ? t('generateRides.ridePattern.unlock') : t('generateRides.ridePattern.lock')}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleLockToggle(field)}
                  disabled={
                    disabled ||
                    (locked[field] ? false : !canLock(field)) ||
                    (field === 'h2h' && h2hDisabled) ||
                    (field === 'h2h' && onlyH2HDisabled)
                  }
                  aria-label={locked[field] ? t('generateRides.ridePattern.unlock') : t('generateRides.ridePattern.lock')}
                >
                  {locked[field] ? <Icon name="lock" size={20} /> : <Icon name="lock" size={20} />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', height: 24, borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
        {segments.map(seg => (
          <Box
            key={seg.label}
            sx={{
              width: `${seg.value}%`,
              background: seg.color,
              display: seg.value > 0 ? 'flex' : 'none',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 700,
              fontSize: 14,
              transition: 'width 0.2s',
            }}
          >
            {/* Always show H2H: 100% label if H2H is 100, even if disabled */}
            {seg.label === 'H2H' && seg.value === 100 ? t('generateRides.ridePattern.h2hFull') : seg.value > 10 ? `${seg.label}: ${seg.value}%` : ''}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default RidePatternEditor; 
